import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';
import {
  buildAllLevelRects,
  clampRect,
  computeAutoCropRect,
  rectToFocal,
  rectToPayload,
  resizeRectFieldAnchored,
  resizeRectFromCorner,
  type CropMeta,
  type CropRect,
  type ResizeCorner,
} from '../../utils/cropUtils';

interface ZoomLevelData {
  level: number;
  imageUrl: string;
  cropMeta?: CropMeta;
}

interface CropEditorData {
  puzzleId: string;
  imageVersion: number;
  originalImageUrl: string;
  width: number;
  height: number;
  zoomLevels: ZoomLevelData[];
}

interface Props {
  puzzleId: string;
  onClose: () => void;
  onApplied: (puzzle: { id: string; zoomLevels: ZoomLevelData[]; imageVersion?: number }) => void;
}

type DragMode = 'move' | `resize-${ResizeCorner}` | null;

const RESIZE_HANDLES: { corner: ResizeCorner; className: string }[] = [
  { corner: 'nw', className: 'admin-crop-handle--nw' },
  { corner: 'ne', className: 'admin-crop-handle--ne' },
  { corner: 'sw', className: 'admin-crop-handle--sw' },
  { corner: 'se', className: 'admin-crop-handle--se' },
];

function rectOverlayStyle(rect: CropRect, scale: number) {
  return {
    left: `${rect.left * scale}px`,
    top: `${rect.top * scale}px`,
    width: `${rect.width * scale}px`,
    height: `${rect.height * scale}px`,
  };
}

function initialFocal(zoomLevels: ZoomLevelData[], imgW: number, imgH: number) {
  const level0 = zoomLevels.find((z) => z.level === 0);
  if (level0?.cropMeta) {
    return rectToFocal(level0.cropMeta, imgW, imgH);
  }
  const meta = zoomLevels[0]?.cropMeta;
  if (meta?.focalX != null && meta?.focalY != null) {
    return { focalX: meta.focalX, focalY: meta.focalY };
  }
  return { focalX: 0.5, focalY: 0.5 };
}

function initialCustom(zoomLevels: ZoomLevelData[]): Partial<Record<number, CropRect>> {
  const custom: Partial<Record<number, CropRect>> = {};
  for (const z of zoomLevels) {
    if (z.cropMeta) {
      custom[z.level] = {
        left: z.cropMeta.left,
        top: z.cropMeta.top,
        width: z.cropMeta.width,
        height: z.cropMeta.height,
      };
    }
  }
  return custom;
}

export function AdminCropEditor({ puzzleId, onClose, onApplied }: Props) {
  const [data, setData] = useState<CropEditorData | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [focalX, setFocalX] = useState(0.5);
  const [focalY, setFocalY] = useState(0.5);
  const [customLevels, setCustomLevels] = useState<Partial<Record<number, CropRect>>>({});
  const [activeLevel, setActiveLevel] = useState(0);
  const [displayScale, setDisplayScale] = useState(1);
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const dragStart = useRef<{ x: number; y: number; rect: CropRect } | null>(null);
  const dragModeRef = useRef<DragMode>(null);
  const displayScaleRef = useRef(1);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setError('');
    api<CropEditorData>(`/admin/puzzles/${puzzleId}/crop-editor`)
      .then((res) => {
        setData(res);
        const focal = initialFocal(res.zoomLevels, res.width, res.height);
        setFocalX(focal.focalX);
        setFocalY(focal.focalY);
        setCustomLevels(initialCustom(res.zoomLevels));
      })
      .catch(() => setError(t('admin.crops.loadError')));
  }, [puzzleId]);

  const levelRects = useMemo(() => {
    if (!data) return {};
    return buildAllLevelRects(data.width, data.height, focalX, focalY, customLevels);
  }, [data, focalX, focalY, customLevels]);

  const activeRect = levelRects[activeLevel];

  const updateDisplayScale = useCallback(() => {
    if (!stageRef.current || !data) return;
    const w = stageRef.current.clientWidth;
    const scale = w / data.width;
    displayScaleRef.current = scale;
    setDisplayScale(scale);
  }, [data]);

  useEffect(() => {
    updateDisplayScale();
    window.addEventListener('resize', updateDisplayScale);
    return () => window.removeEventListener('resize', updateDisplayScale);
  }, [updateDisplayScale, data]);

  const setLevelRect = useCallback((level: number, rect: CropRect, syncFocal = true) => {
    if (!data) return;
    const clamped = clampRect(rect, data.width, data.height);
    setCustomLevels((prev) => ({
      ...prev,
      [level]: clamped,
    }));
    if (syncFocal) {
      const { focalX: fx, focalY: fy } = rectToFocal(clamped, data.width, data.height);
      setFocalX(fx);
      setFocalY(fy);
    }
  }, [data]);

  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);

  useEffect(() => {
    if (!dragMode || !data) return;

    const handlePointerMove = (e: PointerEvent) => {
      const start = dragStart.current;
      const mode = dragModeRef.current;
      if (!start || !mode) return;

      const scale = displayScaleRef.current || 1;
      const dx = (e.clientX - start.x) / scale;
      const dy = (e.clientY - start.y) / scale;

      if (mode === 'move') {
        setLevelRect(activeLevel, {
          ...start.rect,
          left: start.rect.left + dx,
          top: start.rect.top + dy,
        });
      } else if (mode.startsWith('resize-')) {
        const corner = mode.slice(7) as ResizeCorner;
        setLevelRect(
          activeLevel,
          resizeRectFromCorner(start.rect, corner, dx, dy, data.width, data.height),
        );
      }
    };

    const endDrag = () => {
      setDragMode(null);
      dragModeRef.current = null;
      dragStart.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [dragMode, data, activeLevel, setLevelRect]);

  useEffect(() => {
    if (!data || dragMode) return;
    const rect = levelRects[activeLevel];
    if (!rect) return;
    const { focalX: fx, focalY: fy } = rectToFocal(rect, data.width, data.height);
    setFocalX(fx);
    setFocalY(fy);
  }, [activeLevel, data, dragMode, levelRects]);

  const onPointerDown = (e: React.PointerEvent, mode: DragMode) => {
    if (!activeRect) return;
    e.preventDefault();
    e.stopPropagation();
    setDragMode(mode);
    dragModeRef.current = mode;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      rect: { ...activeRect },
    };
  };

  const handleSizeChange = (field: 'width' | 'height', raw: string) => {
    if (!activeRect || !data) return;
    const value = Number.parseInt(raw, 10);
    if (!Number.isFinite(value)) return;
    setLevelRect(
      activeLevel,
      resizeRectFieldAnchored(activeRect, field, value, data.width, data.height),
    );
  };

  const handleAutoFocal = () => {
    if (!data || !activeRect) return;
    const anchor = rectToFocal(activeRect, data.width, data.height);
    setFocalX(anchor.focalX);
    setFocalY(anchor.focalY);
    setCustomLevels({});
  };

  const handleResetLevel = () => {
    if (!data) return;
    const auto = computeAutoCropRect(activeLevel, data.width, data.height, focalX, focalY);
    setLevelRect(activeLevel, auto);
  };

  const handleApply = async () => {
    if (!data) return;
    setSaving(true);
    setError('');
    const anchor = levelRects[0]
      ? rectToFocal(levelRects[0], data.width, data.height)
      : { focalX, focalY };
    try {
      const res = await api<{ puzzle: { id: string; zoomLevels: ZoomLevelData[]; imageVersion?: number } }>(`/admin/puzzles/${puzzleId}/crops`, {
        method: 'POST',
        body: JSON.stringify({
          focalX: anchor.focalX,
          focalY: anchor.focalY,
          levels: rectToPayload(levelRects),
        }),
      });
      onApplied(res.puzzle);
      onClose();
    } catch {
      setError(t('admin.crops.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) {
    return (
      <div className="admin-crop-overlay" role="dialog" aria-modal="true">
        <div className="admin-crop-modal">
          <p className="admin-msg">{error}</p>
          <button type="button" className="btn secondary" onClick={onClose}>{t('admin.crops.cancel')}</button>
        </div>
      </div>
    );
  }

  if (!data || !activeRect) {
    return (
      <div className="admin-crop-overlay" role="dialog" aria-modal="true">
        <div className="admin-crop-modal">
          <p className="admin-msg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const focalDisplay = rectToFocal(activeRect, data.width, data.height);

  const overlayStyle = rectOverlayStyle(activeRect, displayScale);

  const focalStyle = {
    left: `${focalDisplay.focalX * data.width * displayScale}px`,
    top: `${focalDisplay.focalY * data.height * displayScale}px`,
  };

  return (
    <div className="admin-crop-overlay" role="dialog" aria-modal="true">
      <div className="admin-crop-modal">
        <header className="admin-crop-head">
          <h2>{t('admin.crops.title')}</h2>
          <button type="button" className="admin-crop-close" onClick={onClose} aria-label={t('admin.crops.cancel')}>×</button>
        </header>

        <p className="admin-crop-hint">{t('admin.crops.focalHint')}</p>

        <div className="admin-crop-level-tabs">
          {[0, 1, 2, 3, 4].map((lvl) => (
            <button
              key={lvl}
              type="button"
              className={`admin-zoom-btn admin-zoom-btn--compact${activeLevel === lvl ? ' active' : ''}`}
              onClick={() => setActiveLevel(lvl)}
            >
              {lvl}
            </button>
          ))}
        </div>

        <div className="admin-crop-size-row">
          <label className="admin-crop-size-field">
            <span>{t('admin.crops.width')}</span>
            <input
              type="number"
              min={32}
              max={data.width}
              value={activeRect.width}
              onChange={(e) => handleSizeChange('width', e.target.value)}
            />
          </label>
          <label className="admin-crop-size-field">
            <span>{t('admin.crops.height')}</span>
            <input
              type="number"
              min={32}
              max={data.height}
              value={activeRect.height}
              onChange={(e) => handleSizeChange('height', e.target.value)}
            />
          </label>
          <span className="admin-crop-size-hint">
            {activeRect.width}×{activeRect.height} px — {t('admin.crops.level')} {activeLevel}
          </span>
        </div>

        <div
          ref={stageRef}
          className="admin-crop-stage"
        >
          <img
            src={data.originalImageUrl}
            alt=""
            draggable={false}
            onLoad={updateDisplayScale}
          />
          <div className="admin-crop-focal" style={focalStyle} aria-hidden />
          {[0, 1, 2, 3, 4]
            .filter((lvl) => lvl < activeLevel)
            .map((lvl) => {
              const ghostRect = levelRects[lvl];
              if (!ghostRect) return null;
              return (
                <div
                  key={`ghost-${lvl}`}
                  className={`admin-crop-rect admin-crop-rect--ghost admin-crop-rect--ghost-${lvl}`}
                  style={rectOverlayStyle(ghostRect, displayScale)}
                  aria-hidden
                >
                  <span className="admin-crop-rect-label">{lvl}</span>
                </div>
              );
            })}
          <div
            className="admin-crop-rect admin-crop-rect--active"
            style={overlayStyle}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => onPointerDown(e, 'move')}
          >
            <span className="admin-crop-rect-label">{activeLevel}</span>
            {RESIZE_HANDLES.map(({ corner, className }) => (
              <span
                key={corner}
                className={`admin-crop-handle ${className}`}
                onPointerDown={(e) => onPointerDown(e, `resize-${corner}`)}
              />
            ))}
          </div>
        </div>

        <div className="admin-crop-previews">
          {[0, 1, 2, 3, 4].map((lvl) => {
            const r = levelRects[lvl];
            const bgW = data.width / r.width * 100;
            const bgH = data.height / r.height * 100;
            const posX = (r.left / (data.width - r.width)) * 100 || 0;
            const posY = (r.top / (data.height - r.height)) * 100 || 0;
            return (
              <button
                key={lvl}
                type="button"
                className={`admin-crop-preview${activeLevel === lvl ? ' active' : ''}`}
                onClick={() => setActiveLevel(lvl)}
              >
                <div
                  className="admin-crop-preview-inner"
                  style={{
                    backgroundImage: `url(${data.originalImageUrl})`,
                    backgroundSize: `${bgW}% ${bgH}%`,
                    backgroundPosition: `${Number.isFinite(posX) ? posX : 50}% ${Number.isFinite(posY) ? posY : 50}%`,
                  }}
                />
                <span>{lvl}</span>
              </button>
            );
          })}
        </div>

        <div className="admin-crop-actions">
          <button type="button" className="btn secondary admin-btn-sm" onClick={handleAutoFocal}>
            {t('admin.crops.autoFocal')}
          </button>
          <button type="button" className="btn secondary admin-btn-sm" onClick={handleResetLevel}>
            {t('admin.crops.resetLevel')}
          </button>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void handleApply()}>
            {saving ? t('common.loading') : t('admin.crops.apply')}
          </button>
          <button type="button" className="btn secondary" onClick={onClose}>
            {t('admin.crops.cancel')}
          </button>
        </div>

        {error && <p className="admin-msg admin-msg-error">{error}</p>}
      </div>
    </div>
  );
}
